/**
 * What a map, a layer and an audience are — `docs/property-maps-v2.md` §3.2.
 *
 * The geometry lives in `geo.ts`. This is the vocabulary around it: the eight
 * colours, the three audiences, the limits, and the two functions that decide
 * **who sees a layer** and **what that is called**.
 *
 * Both of those are here rather than on either side because they must agree.
 * `layerVisibleTo` is the rule the crew endpoints enforce in SQL and the
 * office page applies in the browser; if the two drifted, a crew would be
 * shown a layer the server would not send, or — much worse — the browser
 * would receive one it then chose not to draw. The server never sends a layer
 * somebody cannot see (§3.4): a layer you cannot see is *absent*, not locked.
 */
import { LAYER_COLORS } from './geo.js';
export { LAYER_COLORS };
export const AUDIENCES = ['all', 'divisions', 'me'];
export const MAP_BASES = ['satellite', 'map', 'hybrid'];
/**
 * The caps, enforced on the server with a sentence rather than a 500.
 *
 * They are not arbitrary: 2,000 shapes is roughly what a phone can draw at
 * 30 fps (§9.5), and 5,000 vertices is a traced outline so detailed that the
 * next drag would be unusable. A person who hits one has done something the
 * feature does not support, and deserves to be told which.
 */
export const MAP_LIMITS = {
    mapsPerProperty: 20,
    layersPerMap: 50,
    featuresPerMap: 2_000,
    verticesPerFeature: 5_000,
};
/**
 * May this viewer see this layer? The only answer, used by both sides.
 *
 * Office: everything except somebody else's *Only me*. Not even an owner or
 * an admin sees another person's scratch work — that is what the choice
 * means, and a feature that quietly exempts administrators is a feature
 * nobody will use for scratch work.
 *
 * Crew: never *Only me*, never a layer hidden on tickets, then *All crews* or
 * a division match. **A crew with no division sees `all` layers only** — not
 * nothing, because the common case of a one-crew company with no divisions
 * set up should still get the plan, and not everything, because a division
 * layer said a division.
 */
export function layerVisibleTo(layer, viewer) {
    if (viewer.kind === 'office') {
        return layer.audience !== 'me' || layer.ownerUserId === viewer.userId;
    }
    if (layer.hiddenOnTickets || layer.audience === 'me')
        return false;
    if (layer.audience === 'all')
        return true;
    return viewer.divisionId !== null && layer.divisionIds.includes(viewer.divisionId);
}
/**
 * What the sharing choice is called, in the one wording every surface uses.
 *
 * `All crews`, `Snow`, `Snow · Hardscape`, `Snow · Hardscape +2`, `Only me`.
 * Two names then a count, because a chip in a layer row has about 140px and
 * four division names do not fit — and because somebody reading a list of
 * layers is scanning for "is this shared widely or narrowly", which two names
 * and a number answers.
 *
 * A division id with no name — deleted, or outside what was loaded — is left
 * out rather than printed as an id (the filter rule, v3 §4). If that empties
 * the list the label falls back to `Specific divisions`, which is true and
 * says nothing misleading.
 */
export function audienceLabel(layer, divisionsById = {}) {
    if (layer.audience === 'all')
        return 'All crews';
    if (layer.audience === 'me')
        return 'Only me';
    const lookup = divisionsById instanceof Map
        ? (id) => divisionsById.get(id)
        : (id) => divisionsById[id];
    const names = layer.divisionIds
        .map((id) => lookup(id))
        .filter((name) => Boolean(name));
    if (names.length === 0)
        return 'Specific divisions';
    if (names.length <= 2)
        return names.join(' · ');
    return `${names.slice(0, 2).join(' · ')} +${names.length - 2}`;
}
/**
 * The three kinds of mark a crew lead may add — §8's segmented control.
 *
 * They are the `glyph` of an ordinary marker feature, not a second kind of
 * record: a mark *is* a shape on a layer, which is why it reaches the office
 * map, its layer and the map's History with no second path to maintain.
 * Three and only three, because a crew standing in a yard picking from a
 * list of twelve is a crew that writes the note and picks the first one.
 */
export const CREW_MARK_GLYPHS = ['obstacle', 'hazard', 'note'];
/**
 * What a mark is called when nobody named it.
 *
 * A marker's `name` is what the office map's layer row and the shape popup
 * print, and a crew sheet has no name field — §8 asks for a glyph, a note
 * and a photo. So the glyph supplies the name, and the note stays the note.
 */
export const MARK_NAMES = {
    obstacle: 'Obstacle',
    hazard: 'Hazard',
    note: 'Note',
};
/**
 * What a crew is told about who else can see a layer — §8.
 *
 * `audienceLabel` is a **chip**: it has 140px in a layer row and answers "is
 * this shared widely or narrowly" at a glance. This is a **sentence** under a
 * layer's name on a phone a crew lead is holding, and it answers a different
 * question — *who else is reading what I am about to add to this?* — so it
 * spells the divisions out rather than trailing a `+2`, and it never says
 * *Only me*, because a crew is never shown one.
 *
 * The four shapes are §8's own words, including the one asymmetry in them:
 * a single division is `Shared with Snow crews` and a pair is `Shared with
 * Snow and Hardscape`. "Snow and Hardscape crews" would read as one crew that
 * does both.
 *
 * A division id with no name is dropped rather than printed, the same rule
 * `audienceLabel` follows; if that empties the list the sentence falls back
 * to the honest `Shared with specific crews`.
 */
export function crewAudienceSentence(layer, divisionsById = {}) {
    if (layer.audience === 'all')
        return 'Shared with all crews';
    /* A crew never sees one, so there is no wording for it — but a caller
       holding a mixed list should get something true rather than a throw. */
    if (layer.audience === 'me')
        return 'Not shared with crews';
    const lookup = divisionsById instanceof Map
        ? (id) => divisionsById.get(id)
        : (id) => divisionsById[id];
    const names = layer.divisionIds
        .map((id) => lookup(id))
        .filter((name) => Boolean(name));
    if (names.length === 0)
        return 'Shared with specific crews';
    if (names.length === 1)
        return `Shared with ${names[0]} crews`;
    if (names.length === 2)
        return `Shared with ${names[0]} and ${names[1]}`;
    const rest = names.length - 2;
    return `Shared with ${names[0]}, ${names[1]} and ${rest} more`;
}
/**
 * How far a part's stored value may drift from its shape before the row says
 * **map changed** (§3.3).
 *
 * Half a percent, not zero: the same polygon re-measured by a slightly
 * different Turf version, or a value rounded on the way into
 * `numeric(14,3)`, can differ in the last digit, and a permanent "map
 * changed" badge on a part nobody has touched is a badge people learn to
 * ignore.
 */
export const MAP_CHANGED_TOLERANCE = 0.005;
/** Whether a part's number still matches the shape it came from. */
export function hasMapChanged(partValue, shapeValue) {
    if (shapeValue === null)
        return false;
    if (partValue === 0)
        return shapeValue !== 0;
    return Math.abs(shapeValue - partValue) / partValue > MAP_CHANGED_TOLERANCE;
}
/** What a measurement total's provenance is, given its parts' (§3.3). */
export function rollUpSource(sources) {
    if (sources.length === 0)
        return 'typed';
    const every = (match) => sources.every(match);
    /* A shape is an aerial measurement by another name, so the two roll up
       together rather than making every map-drawn total read as "mixed". */
    if (every((one) => one === 'aerial' || one === 'map_layer'))
        return 'aerial';
    if (every((one) => one === 'walked'))
        return 'walked';
    if (every((one) => one === 'typed'))
        return 'typed';
    return 'mixed';
}
//# sourceMappingURL=property-maps.js.map